import type { ChangeEvent, ComponentPropsWithRef } from 'react';
import type { DropEvent } from 'react-aria';

import { assignInlineVars } from '@vanilla-extract/dynamic';
import { useRef, useState } from 'react';
import { isFileDropItem, useButton, useDrop } from 'react-aria';
import { Trans, useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import { srOnlyStyle } from '~/styles/utilities.css';
import { clsx } from '~/styles/utils';
import { filterBoolean } from '~/utils/arrays';

import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { Icon } from '../Icon/Icon';
import { ButtonLink } from '../Link/ButtonLink';
import { Text } from '../Text/Text';
import {
  dropzoneDescriptionStyle,
  dropzoneDisabledStyle,
  dropzoneDraggingStyle,
  dropzoneHintStyle,
  dropzoneRoundStyle,
  dropzoneStyle,
  dropzoneWrapperStyle,
} from './FileDropzone.css';
import { useFileInput } from './FileDropzone.hooks';
import { avatarSizeVar } from './UploadedAvatar.css';

type FileRejection = {
  name: string;
  reason: 'size' | 'type';
};

type Props = Omit<ComponentPropsWithRef<'div'>, 'onSelect' | 'ref'> & {
  accept?: `.${Lowercase<string>}`[];
  disabled?: boolean;
  maxFileSizeInMegaByte?: number;
  multiple?: boolean;
  onSelect: (files: File[]) => void;
  shape?: 'rectangle' | 'round';
  size?: number;
};

export const FileDropzone = ({
  accept = [],
  disabled,
  maxFileSizeInMegaByte,
  multiple,
  onSelect,
  shape = 'rectangle',
  size = 300,
  ...delegated
}: Props) => {
  const { t } = useTranslation(['common']);

  const [rejections, setRejections] = useState<FileRejection[]>([]);

  const dropzoneRef = useRef<HTMLDivElement>(null);
  const dropButtonRef = useRef<HTMLButtonElement>(null);

  const { inputRef, openFileDialog, resetFileInput } = useFileInput();

  const handleFiles = (files: File[]) => {
    setRejections([]);

    const acceptedFiles = files.reduce((acc, file) => {
      if (accept.length > 0 && !accept.some((extension) => file.name.toLowerCase().endsWith(extension))) {
        setRejections((rejections) => [...rejections, { name: file.name, reason: 'type' }]);
        return acc;
      }

      if (maxFileSizeInMegaByte !== undefined && file.size > maxFileSizeInMegaByte * 1024 * 1024) {
        setRejections((rejections) => [...rejections, { name: file.name, reason: 'size' }]);
        return acc;
      }

      acc.push(file);

      return acc;
    }, [] as File[]);

    if (acceptedFiles.length > 0) onSelect(multiple ? acceptedFiles : [acceptedFiles[0]]);
  };

  const handleDrop = async (event: DropEvent) => {
    const files = (await Promise.all(event.items.map((item) => (isFileDropItem(item) ? item.getFile() : null)))).filter(filterBoolean);

    handleFiles(files);
  };

  const { dropButtonProps, dropProps, isDropTarget } = useDrop({
    hasDropButton: true,
    isDisabled: disabled,
    onDrop: handleDrop,
    ref: dropzoneRef,
  });

  const { buttonProps } = useButton(dropButtonProps ?? {}, dropButtonRef);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) handleFiles(Array.from(event.target.files));
    resetFileInput();
  };

  const maxSizeHint = maxFileSizeInMegaByte === undefined ? '' : t('common:fileUpload.maxSize', { max: maxFileSizeInMegaByte });
  const acceptHint = accept.map((extension) => extension.slice(1)).join(', ');
  const hint = [acceptHint, maxSizeHint].filter(Boolean).join(' · ');

  return (
    <div
      {...delegated}
      className={clsx(dropzoneWrapperStyle, delegated.className)}
      style={shape === 'round' ? { ...assignInlineVars({ [avatarSizeVar]: `${size}px` }), ...delegated.style } : delegated.style}
    >
      <div
        {...dropProps}
        className={clsx(
          dropzoneStyle,
          shape === 'round' && dropzoneRoundStyle,
          isDropTarget && dropzoneDraggingStyle,
          disabled && dropzoneDisabledStyle,
        )}
        ref={dropzoneRef}
      >
        <button {...buttonProps} className={srOnlyStyle} ref={dropButtonRef} type="button">
          {t('common:fileUpload.dropTarget')}
        </button>

        <Icon name="Upload" size={32} />

        <Text className={dropzoneDescriptionStyle}>
          <Trans
            components={{ browse: <ButtonLink disabled={disabled} onClick={openFileDialog} type="button" /> }}
            i18nKey={shape === 'round' ? 'common:fileUpload.avatarPrompt' : 'common:fileUpload.filePrompt'}
          />
        </Text>

        {!!hint && <Text className={dropzoneHintStyle}>{hint}</Text>}

        <input
          accept={accept.join(',') || undefined}
          disabled={disabled}
          hidden
          multiple={multiple}
          onChange={handleInputChange}
          ref={inputRef}
          type="file"
        />
      </div>

      {rejections.map(({ name, reason }) => (
        <ErrorMessage key={name}>
          {match(reason)
            .with('size', () => t('common:fileUpload.rejectedSize', { max: maxFileSizeInMegaByte, name }))
            .with('type', () => t('common:fileUpload.rejectedType', { name }))
            .exhaustive()}
        </ErrorMessage>
      ))}
    </div>
  );
};
